/**
 * The festival screen's vocabulary: the labels the list and the page show, the day-range format, and the plain
 * sentence for each 409 the festival routes can answer.
 *
 * `@/types/tina-festivals` and `@/types/tina-festival-page` own the wire shapes; this module owns what a curator reads.
 * Every label map is a `Record<Union, string>`, so a member added to a union without a label is a compile error, not a
 * blank cell.
 *
 * Every render path degrades instead of throwing: a 409 string this build does not know comes back raw, and a date
 * that does not parse comes back as the string it arrived as. The orchestrator can be newer than this build.
 */
import type {
  ChoiceOutcome,
  FestivalKind,
  FestivalRecordState,
  MatchedBy,
} from "@/types/tina-festivals";

/** The token a `CONFLICT_TEXT` sentence uses for the part of the 409 after its colon. */
const VALUE_TOKEN = "{value}";

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** What kind of festival a record is, as a curator says it: one ticket per day, one program of shows, or a tour. */
export const FESTIVAL_KIND_LABEL: Record<FestivalKind, string> = {
  day_ticket: "Day ticket",
  program: "Program",
  unclear: "Unclear",
  tour: "Tour",
};

export const RECORD_STATE_LABEL: Record<FestivalRecordState, string> = {
  proposed: "Proposed",
  confirmed: "Confirmed",
  auto_grouped: "Grouped automatically",
  rejected: "Rejected",
  retired: "Merged into another",
};

export const CHOICE_OUTCOME_LABEL: Record<ChoiceOutcome, string> = {
  folded: "Merged into the kept event",
  deleted: "Deleted",
  kept_has_saves: "Not deleted: people saved it",
  gone: "Already gone",
  detached: "Taken out of the group",
};

/** How the festival job found a Mood event that already sits in the festival's place. */
export const MATCHED_BY_LABEL: Record<MatchedBy, string> = {
  page: "Found through its page",
  alias: "Found by the festival's name",
  acts: "Found by its acts",
};

/**
 * One plain sentence per 409 string the festival routes serve, keyed by the part before the first `:`. A value
 * after the colon (an id, a day, a state, a url) fills `{value}`. A string not listed here is shown raw.
 */
export const CONFLICT_TEXT: Record<string, string> = {
  card_superseded:
    "This card was closed because the festival changed, so your answer was not used.",
  skeleton_changed:
    "This festival changed while you worked. The page reloaded and kept your edits where they still fit.",
  record_state: "This festival's state ({value}) does not allow this action.",
  day_conflict:
    "The festival already has this venue on {value}, so the day cannot move there.",
  alias_is_another_record:
    "That name belongs to another festival: merge them instead.",
  held_by_other_record:
    "That event belongs to another festival, so it cannot be kept here.",
  same_record: "A festival cannot be merged into itself.",
  different_city:
    "These festivals are in different cities, so they cannot be merged.",
  source_has_group:
    "A festival with a Mood group cannot be merged away: a merge never moves a group.",
  no_pages: "Choose at least one page to move out.",
  unknown_page: "{value} is not a page of this festival's proposal.",
  all_pages:
    "Every page would leave this festival. Keep at least one page in it.",
  no_group: "This festival has no Mood group to undo.",
  same_kind: "The festival is already that kind.",
  festival_hold_off:
    "Approving is off: Tina's festival hold is not switched on yet. Nothing was saved.",
  festival_job_off:
    "Tina's festival job is off, so nothing can be saved now. Your edits are still here.",
  page_changed:
    "This festival changed while you worked. The page reloaded and kept your edits where they still fit.",
  publishing:
    "Tina is still publishing. Wait until it is done, then press Publish changes again.",
  suggestion_closed: "That suggestion was already answered.",
  suggestion_not_found: "That suggestion is gone.",
  use_as_day_taken:
    "{value} already has its event in Mood. Choose Replace or Not part.",
  unknown_line: "That event is no longer listed. Reload the page.",
  not_published:
    "This festival is not in Mood, so there is nothing to unpublish.",
  published_use_unpublish:
    "This festival is published. Use Unpublish from Actions instead.",
  unpublished_use_publish_changes:
    "This festival was taken out of Mood. Approve and publish puts it back.",
  unpublished_record:
    "This festival was taken out of Mood. Approve it again before you change its kind.",
};

/**
 * How a 409 value reads inside its sentence, for the keys whose value is something a curator can read: a record
 * state's label (the raw value when this build does not know it) and a day (`13 Dec 2026`). Any other key's value
 * goes in as it arrived.
 */
const CONFLICT_VALUE_TEXT: Record<string, (value: string) => string> = {
  record_state: (value) =>
    Object.hasOwn(RECORD_STATE_LABEL, value)
      ? RECORD_STATE_LABEL[value as FestivalRecordState]
      : value,
  day_conflict: (value) => dayRange(value, value) || value,
  use_as_day_taken: (value) => dayRange(value, value) || value,
};

/**
 * A `YYYY-MM-DD` day as `13 Dec 2026`, or `11-12 Dec 2026` for a range inside one month, or `30 Nov - 1 Dec 2026`
 * across two. One day (`first` equals `last`), or only one end given, reads as one day. Neither end: `""`.
 */
export function dayRange(first: string | null, last: string | null): string {
  const start = first ?? last;
  const end = last ?? first;
  if (start === null || end === null) return "";
  if (start === end) return formatDay(start);
  const from = parseDay(start);
  const to = parseDay(end);
  if (from === null || to === null)
    return `${formatDay(start)} - ${formatDay(end)}`;
  if (from.year === to.year && from.month === to.month) {
    return `${from.day}-${to.day} ${MONTHS[from.month - 1]} ${from.year}`;
  }
  if (from.year === to.year) {
    return `${from.day} ${MONTHS[from.month - 1]} - ${to.day} ${MONTHS[to.month - 1]} ${from.year}`;
  }
  return `${formatDay(start)} - ${formatDay(end)}`;
}

/**
 * The sentence for a 409 `detail`. Split on the FIRST colon only, because a url carries colons of its own. A key this
 * build does not know comes back as the whole string, so a server string added later still shows.
 */
export function conflictText(detail: string): string {
  const colon = detail.indexOf(":");
  const key = colon === -1 ? detail : detail.slice(0, colon);
  if (!Object.hasOwn(CONFLICT_TEXT, key)) return detail;
  const raw = colon === -1 ? "" : detail.slice(colon + 1);
  const value = Object.hasOwn(CONFLICT_VALUE_TEXT, key)
    ? CONFLICT_VALUE_TEXT[key](raw)
    : raw;
  return CONFLICT_TEXT[key].split(VALUE_TOKEN).join(value);
}

export interface DayParts {
  year: number;
  month: number;
  day: number;
}

/** The parts of a `YYYY-MM-DD` day, read as written (no time zone), or null when it is not one. */
export function parseDay(iso: string): DayParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(match[1]), month, day: Number(match[3]) };
}

/** `13 Dec 2026`, with no leading zero on the day; the input as it arrived when it does not parse. */
function formatDay(iso: string): string {
  const parts = parseDay(iso);
  if (parts === null) return iso;
  return `${parts.day} ${MONTHS[parts.month - 1]} ${parts.year}`;
}

/** A label by its value, or the value as it arrived when this build has no label for it. */
export function labelOf<K extends string>(
  labels: Record<K, string>,
  value: string,
): string {
  return Object.hasOwn(labels, value) ? labels[value as K] : value;
}
